<template>
  <v-dialog
    v-model="show"
    max-width="800px"
  >
    <v-form ref="form" v-model="valid" :disabled="loading" @submit="saveElement" onsubmit="return false;">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isNew ? $t('editor.element.create') :  $t('editor.element.update') }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="show = false"
          >
          <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col
                :cols="`{{ showLogicalLanguage ? 12 : 5 }}`"
              >
                <v-text-field
                  :label="$t('editor.element.name')"
                  required
                  v-model="elementForm.name"
                  outlined
                  clearable
                  @click:clear="elementForm.name = ''"
                  counter="100"
                  maxLength="100"
                  :rules="nameRule"
                  autofocus
                ></v-text-field>
              </v-col>
              <v-col
                cols="5"
                v-if="showLogicalLanguage"
              >
                <v-text-field
                  :label="$t('editor.element.prolog')"
                  v-model="elementForm.prolog"
                  outlined
                  clearable
                  counter="200"
                  maxLength="200"
                  :rules="prologRule"
                  @click:clear="elementForm.prolog = ''; suggestDefaultProlog = true"
                >
                </v-text-field>
              </v-col>
              <v-col
                cols="2"
                v-if="showLogicalLanguage"
              >
                <v-checkbox
                  v-model="elementForm.isParameter"
                  :label="$t('editor.element.parameter')"
                  hide-details
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row v-if="elementForm.createdBy">
              <v-col cols="12">
                <div>{{ $t('editor.createdBy') }} <strong>{{ elementForm.createdBy }}</strong>, {{ $t('editor.the') }} <strong>{{ new Date(elementForm.createdDate).toLocaleString() }}</strong></div>
                <div>{{ $t('editor.lastModifiedBy') }}  <strong>{{ elementForm.lastModifiedBy }}</strong>, {{ $t('editor.the') }} <strong>{{ new Date(elementForm.lastModifiedDate).toLocaleString() }}</strong></div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <template v-if="showLogicalLanguage && additionnalElements.length > 0">
          <v-card-title>
            <span class="text-h6">{{ $t('editor.element.subElements') }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row v-for="(additionalElement, index) in additionnalElements" :key="`ad${index}`">
                <v-col>
                  <v-text-field
                    :label="$t('editor.element.name')"
                    required
                    v-model="additionalElement.name"
                    outlined
                    clearable
                    counter="100"
                    maxLength="100"
                    :rules="nameRule"
                    :disabled="!!additionalElement.id"
                  ></v-text-field>
                </v-col>
                <v-col cols="5">
                  <v-text-field
                    :label="$t('editor.element.prolog')"
                    v-model="additionalElement.prolog"
                    outlined
                    counter="200"
                    maxLength="200"
                    :rules="prologRule"
                    :disabled="true"
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    v-model="additionalElement.isParameter"
                    :label="$t('editor.element.parameter')"
                    hide-details
                    :disabled="!!additionalElement.id"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </template>
        <v-card-actions>
          <v-btn 
            v-if="!isNew && !disableDelete"
            class="raison-btn"
            @click="deleteElement"
            :disabled="loading"
            alt="delete"
          >
            <v-icon dark>
              mdi-trash-can-outline
            </v-icon>
            {{ $t('editor.element.delete') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            type="submit"
            class="raison-btn"
            :disabled="loading || !valid"
            :loading="loading"
          >
            <v-icon dark>
              mdi-content-save-outline
            </v-icon>
            {{ $t('editor.element.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import elementService from '@/services/elements.js'
import translationsService from '@/services/translations.js'
import naturalOrLogicalLanguageMixin from '@/mixins/natural-or-logical-language-mixin.js';

export default ({
  name: 'ElementForm',

  components: {
  },

  mixins: [ naturalOrLogicalLanguageMixin ],

  data () {
   return {
      loading: false,
      elementForm: {name:'', prolog:'', isParameter: true},
      additionnalElements: [],
      valid: false,
      name: this.element.name,
      nameRule: [
        v => !!v || this.$t('global.required'),
      ],
      prolog: this.element.prolog,
      prologRule: [
        v => /^[a-zA-Z].{0,199}$/.test(v) || this.$t('editor.element.invalid'),
        v => v === '' || !/:-/.test(v) || this.$t('editor.element.ruleNotAllowed'),
      ],
      suggestDefaultProlog: true,
    }
  },

  watch: {
    element: function(element) {
      this.elementForm = {
        ...this.elementForm,
        ...element
      }
      this.suggestDefaultProlog = !this.elementForm.id
    },
    "elementForm.name": function(value) {
      if(value && this.shouldSuggestDefaultPrologTranslation && this.showLogicalLanguage) {
        this.elementForm.prolog = translationsService.getDefaultTranslation(value)
      }
    },
    "elementForm.prolog": function(value) {
      this.checkAdditionnalElements(value)
    }
  },

  props: {
    open: {
      type: Boolean,
      required: true,
    },
    element: {
      type: Object,
      required: false,
      default: () => { return {name:'', prolog:'', isParameter:true } }
    },
    disableDelete: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  
  computed: {
    isNew() {
      return !this.elementForm.id
    },
    shouldSuggestDefaultPrologTranslation() {
      return this.isNew || this.suggestDefaultProlog
    },
    show: {
      get () {
          return this.open;
      },
      set (value) {
        if(!value) {
          this.onCancel()
        }
        this.$emit('update:open', value);
      }
    }
  },

  methods: {
    checkAdditionnalElements(element) {
      // console.log('checkAdditionnalElements', element)
      if(!element) {
        this.additionnalElements = []
        return
      }
      const projectId = this.$route.params.id
      const prologs = elementService.checkAdditionnalElements(element)
      Promise.allSettled(elementService.findElementsByProlog(projectId, prologs))
        .then(responses => {
          this.additionnalElements = responses
            .map((response, index) => response.value.data[0] || {name:'', prolog:prologs[index], isParameter: true})
        })
        .catch(err => {
          console.log('checkAdditionnalElements', err)
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
    },
    saveElement(event) {
      event.preventDefault()
      if(!this.$refs.form.validate()) {
        return
      }
      if(this.isNew) {
        this.createElement()
      }
      else {
        this.updateElement()
      }
      this.createAdditionnalElement()
    },
    createAdditionnalElement() {
      const projectId = this.$route.params.id
      const creationRequests = this.additionnalElements
        .filter(element => !element.id)
        .map(element => elementService.createElement(projectId, element))
      Promise.allSettled(creationRequests)
        .then(responses => {
          // this.onCancel()
          const creations = responses.filter(response => response.status === 'fulfilled')
          this.additionnalElements = this.additionnalElements.filter(element => !!element.id)
          this.additionnalElements
            .splice(this.additionnalElements.length, 0, ...creations.map(response => response.value.data))
          this.$emit("elementsCreated", creations.map(response => response.value.data))
          // errors will be handled by the createElement method
          const failures = responses.filter(response => response.status === 'rejected')
            .map(response => response.reason.response.data.message)
          // console.log('createAdditionnalElement failures', failures)
          if(failures.length > 0) {
            this.$emit('raison-event', { type: 'error', messages: failures })
          }
        })
        .catch(err => {
          console.log('createAdditionnalElement', err)
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
    },
    createElement() {
      const projectId = this.$route.params.id
      const newElement = { ... this.elementForm }
      this.loading = true
      elementService.createElement(projectId, newElement)
        .then(response => {
          this.$emit("elementsCreated", [response.data])
          this.show = false
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
        .finally(() => {
            this.loading = false
        })
    },
    updateElement() {
      const projectId = this.$route.params.id
      const newElement = { ... this.elementForm }
      this.loading = true
      elementService.updateElement(projectId, newElement)
        .then(response => {
          this.$emit("elementsUpdated", [response.data])
          this.show = false
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
        .finally(() => {
            this.loading = false
        })
    },
    deleteElement() {
      this.loading = true
      const projectId = this.$route.params.id
      const elementToDelete = this.element
      elementService.deleteElement(projectId, elementToDelete.id)
        .then(() => {
          this.$emit("elementsDeleted", [elementToDelete])
          this.show = false
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
        .finally(() => {
          this.loading = false
        })
    },
    onCancel() {
      // console.log('onCancel')
      this.suggestDefaultProlog = false
      this.elementForm = {...this.element}
      this.$refs.form.resetValidation()
    }
  },

});
</script>
<style scoped>
</style>